import { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowDown, MapPin } from 'lucide-react';
import { MaskedReveal } from './motion/Reveal';
import { BRAND, FACILITY } from '../data/content';

const Hero = () => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] });
    const y = useTransform(scrollYProgress, [0, 1], ['0%', '30%']);
    const scale = useTransform(scrollYProgress, [0, 1], [1.06, 1.22]);
    const overlay = useTransform(scrollYProgress, [0, 1], [0.55, 0.9]);

    return (
        <section
            id="top"
            ref={ref}
            data-testid="hero-section"
            className="relative h-[100svh] w-full overflow-hidden bg-[#0A0A0B] text-white grain"
        >
            {/* Parallax background */}
            <motion.div
                style={{ y, scale }}
                className="absolute inset-0 will-change-transform"
            >
                <img
                    src={FACILITY.hero}
                    alt="Indian Industrial Distributors — shop floor"
                    className="w-full h-full object-cover"
                />
            </motion.div>
            <motion.div style={{ opacity: overlay }} className="absolute inset-0 hero-vignette pointer-events-none" />

            {/* Blueprint grid */}
            <div
                className="absolute inset-0 pointer-events-none opacity-[0.06]"
                style={{
                    backgroundImage:
                        'linear-gradient(rgba(255,255,255,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.4) 1px, transparent 1px)',
                    backgroundSize: '80px 80px',
                }}
            />

            {/* Top meta bar */}
            <div className="absolute top-[88px] left-0 right-0 z-10">
                <div className="mx-auto max-w-[1500px] px-6 md:px-10 flex items-center justify-between text-white/60">
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 1.5, duration: 0.8 }}
                        className="overline"
                    >
                        {BRAND.established} · THANE, INDIA
                    </motion.div>
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 1.5, duration: 0.8 }}
                        className="hidden md:flex items-center gap-2 overline"
                    >
                        <span className="w-1.5 h-1.5 bg-[#FF3B00] rounded-full animate-pulse" />
                        Live · shop floor operational
                    </motion.div>
                </div>
            </div>

            {/* Center headline */}
            <div className="relative z-10 h-full mx-auto max-w-[1500px] px-6 md:px-10 flex flex-col justify-end pb-16 md:pb-24">
                <MaskedReveal
                    delay={0.35}
                    stagger={0.11}
                    className="h-display text-white text-[13vw] md:text-[9.2vw] leading-[0.86]"
                    textClass=""
                    lines={['INDIAN', 'INDUSTRIAL', <span key="d">DISTRIBUTORS<span className="text-[#FF3B00]">.</span></span>]}
                />

                <div className="mt-10 grid grid-cols-12 gap-6 items-end">
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 1.35, duration: 0.9 }}
                        className="col-span-12 md:col-span-6 lg:col-span-5 text-white/75 text-base md:text-lg max-w-md"
                    >
                        Precision bright bars, cold drawn, peeled and ground for India's most demanding OEMs — since 1985.
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 1.5, duration: 0.9 }}
                        className="col-span-12 md:col-span-6 lg:col-span-4 lg:col-start-9 flex items-center gap-4"
                    >
                        <a
                            href="#contact"
                            data-testid="hero-cta-quote"
                            className="btn-invert inline-flex items-center justify-center gap-2 h-14 px-8 bg-[#FF3B00] text-white text-[11px] font-mono uppercase tracking-[0.24em] hover:text-[#FF3B00]"
                        >
                            Request quote →
                        </a>
                        <a
                            href="#products"
                            data-testid="hero-cta-catalog"
                            className="inline-flex items-center justify-center gap-2 h-14 px-6 border border-white/30 text-white text-[11px] font-mono uppercase tracking-[0.24em] hover:border-white hover:bg-white hover:text-[#0A0A0B] transition-colors"
                        >
                            View Catalog
                        </a>
                    </motion.div>
                </div>
            </div>

            {/* Bottom scroll ticker */}
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.8, duration: 1 }}
                className="absolute bottom-6 left-0 right-0 z-10"
            >
                <div className="mx-auto max-w-[1500px] px-6 md:px-10 flex items-center justify-between text-white/50">
                    <a href="#work" className="flex items-center gap-3 overline hover:text-white transition-colors">
                        <ArrowDown size={14} />
                        Scroll · discover
                    </a>
                    <span className="hidden md:flex items-center gap-2 overline">
                        <MapPin size={12} /> 19.2011° N · 72.9615° E
                    </span>
                </div>
            </motion.div>
        </section>
    );
};

export default Hero;
